// ds-color-ignore-file: dev-only / internal tooling surface
"use client"

import { useEffect, useState } from "react"
import type { StringStatus, StringApproval } from "@/lib/review/string-status"

/** Fetch tier + approvals for a key. `null` key (unresolved) → idle.
 *  `error` true when the fetch failed (drives the grid's fail-open). */
export function useStringStatus(
  key: string | null,
  idToken?: string
): {
  status: StringStatus | null
  error: boolean
} {
  const [status, setStatus] = useState<StringStatus | null>(null)
  const [error, setError] = useState(false)
  useEffect(() => {
    // Need both a resolved key and an auth token; without the token the review
    // status API would 401, so we simply don't fetch (no gate/approval shown).
    if (!key || !idToken) {
      setStatus(null)
      setError(false)
      return
    }
    let cancelled = false
    setStatus(null)
    setError(false)
    fetch(`/api/review/string-status?key=${encodeURIComponent(key)}`, {
      headers: { "User-Authorization": `Bearer ${idToken}` },
    })
      .then(async (res) => {
        if (!res.ok) throw new Error(String(res.status))
        return (await res.json()) as StringStatus
      })
      .then((s) => {
        if (!cancelled) setStatus(s)
      })
      // Surface (not swallow) a failed fetch as `error` — the grid reads it to
      // mark every locale "unconfirmed" (fail-open) rather than show a false lock.
      .catch(() => {
        if (!cancelled) setError(true)
      })
    return () => {
      cancelled = true
    }
  }, [key, idToken])
  return { status, error }
}

const GATE: Record<string, { glyph: string; label: string; note: string }> = {
  legal: {
    glyph: "⚖",
    label: "Legal copy",
    note: "your edit goes to legal review before it's published.",
  },
  glossary: {
    glyph: "📖",
    label: "Glossary term",
    note: "your edit goes to review before it's published.",
  },
  "high-traffic": {
    glyph: "⬆",
    label: "High-traffic copy",
    note: "your edit goes to review before it's published.",
  },
}

/** ⚖/📖/⬆ badge + one-line consequence note. Nothing when the string isn't gated. */
export function GateNote({ tier }: { tier: StringStatus["tier"] }) {
  if (!tier) return null
  const g = GATE[tier]
  if (!g) return null
  return (
    <div
      style={{
        fontSize: 11.5,
        lineHeight: 1.35,
        color: "#8a5a00",
        background: "#fff4e3",
        border: "1px solid #f0d9b0",
        borderRadius: 6,
        padding: "5px 8px",
        marginBottom: 10,
      }}
    >
      <strong style={{ fontWeight: 700 }}>
        {g.glyph} {g.label}
      </strong>{" "}
      — {g.note}
    </div>
  )
}

/** Compact "x days ago" — no dependency. */
export function formatRelative(iso: string): string {
  const then = new Date(iso).getTime()
  const secs = Math.max(0, Math.round((Date.now() - then) / 1000))
  if (secs < 60) return "just now"
  const mins = Math.round(secs / 60)
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.round(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.round(hrs / 24)
  if (days < 30) return `${days}d ago`
  const months = Math.round(days / 30)
  if (months < 12) return `${months}mo ago`
  return `${Math.round(months / 12)}y ago`
}

/** Per-locale inline mark: 🔒 when a sign-off is fresh, ⚠ stale otherwise. */
export function ApprovalMark({ approval }: { approval: StringApproval }) {
  const fresh = approval.fresh
  return (
    <div
      title={`${approval.reviewerEmail} · ${formatRelative(approval.updatedAt)}${
        fresh ? "" : " · stale (English changed since sign-off)"
      }`}
      style={{
        fontSize: 11,
        fontWeight: 700,
        marginTop: 2,
        color: fresh ? "#2f6f3e" : "#b25e00",
      }}
    >
      {fresh ? "🔒 approved" : "⚠ stale"}
    </div>
  )
}

/** Header summary chip — focus locale's sign-off + expandable per-locale list.
 *  Renders nothing when there are no approvals. */
export function ApprovalsChip({
  approvals,
  focusLocale,
}: {
  approvals: StringApproval[]
  focusLocale?: string
}) {
  const [open, setOpen] = useState(false)
  if (approvals.length === 0) return null
  const primary =
    approvals.find((a) => a.locale === focusLocale) ?? approvals[0]
  const others = approvals.length - 1
  return (
    <div style={{ marginBottom: 8 }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          border: "1px solid #cfe6d6",
          background: "#eef7f0",
          color: "#2f6f3e",
          borderRadius: 999,
          padding: "3px 9px",
          fontSize: 11,
          fontWeight: 700,
          cursor: "pointer",
        }}
      >
        {primary.fresh ? "🔒" : "⚠"} Approved by{" "}
        {primary.reviewerEmail.split("@")[0]} ·{" "}
        {formatRelative(primary.updatedAt)}
        {others > 0 ? ` (+${others})` : ""}
      </button>
      {open ? (
        <div
          style={{
            marginTop: 6,
            border: "1px solid #e4e4e8",
            borderRadius: 8,
            padding: "6px 8px",
            fontSize: 11,
            color: "#444",
          }}
        >
          {approvals.map((a) => (
            <div
              key={a.locale}
              style={{ display: "flex", gap: 8, padding: "2px 0" }}
            >
              <strong style={{ width: 52, flexShrink: 0 }}>
                {a.locale.toUpperCase()}
              </strong>
              <span style={{ flex: 1 }}>{a.reviewerEmail}</span>
              <span style={{ color: a.fresh ? "#2f6f3e" : "#b25e00" }}>
                {a.fresh ? "approved" : "stale"} · {formatRelative(a.updatedAt)}
              </span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  )
}
